/* eslint-disable react/no-unused-prop-types */
import { useCallback, useMemo } from 'react';
import { useReduxForm } from '@rainbow-modules/hooks';
import { CheckboxGroupProps } from 'react-rainbow-components/components/CheckboxGroup';
import LoadingShape from 'components/LoadingShape';
import UserAvatar from 'components/UserAvatar';
import { isEmpty } from '@rainbow-modules/validation';
import getDisplayName from 'data/services/profile/getDisplayName';
import { ItemUser } from 'data/firestore/globals';
import useHttpQuery from 'data/firestore/useHttpQuery';
import getInitials from 'data/services/string/getInitials';
import { MemberInfo, StyledCheckBoxGroup } from './styled';

interface TeamMember extends ItemUser {
    status: string;
}

interface TeamMemberSelectProps extends Omit<CheckboxGroupProps, 'value' | 'onChange'> {
    agentId?: string;
    value?: Array<ItemUser | string> | null;
    onChange?: (value: Array<ItemUser> | null) => void;
    maxSelected?: number;
}

const TeamMemberSelect = ({
    agentId = '',
    maxSelected,
    ...props
}: TeamMemberSelectProps) => {
    const {
        value: valueInProps,
        onChange = () => {},
        ...rest
    } = useReduxForm(props);

    const { data = [], isFetched } = useHttpQuery<TeamMember[]>({
        pathname: `/team/${agentId}/members`,
        queryOptions: {
            enabled: Boolean(agentId),
        },
    });

    const members = useMemo(
        () => data.filter((member) => member.status === 'active'),
        [data],
    );

    const value = useMemo(
        () => {
            const values = valueInProps?.filter((val) => !isEmpty(val));
            if (values) {
                if (values.length === 0) return [];
                const firstValue = values.at(0);
                return (
                    typeof firstValue === 'string'
                        ? values
                        : (values as ItemUser[]).map((member) => member.uid)
                ) as string[];
            }
            return [];
        },
        [valueInProps],
    );

    const options = useMemo(
        () => members.map(
            (member) => {
                const displayName = getDisplayName(member);
                const initials = (
                    displayName !== 'Unknown'
                        ? getInitials(displayName)
                        : undefined
                );
                return {
                    value: member.uid,
                    label: (
                        <MemberInfo
                            icon={(
                                <UserAvatar
                                    size="x-small"
                                    initials={initials}
                                    value={member.uid}
                                    src={member.photoURL}
                                />
                            )}
                            title={displayName}
                        />
                    ),
                    // eslint-disable-next-line max-len
                    disabled: value.length === maxSelected && !value.includes(member.uid as string),
                };
            },
        ),
        [maxSelected, members, value],
    );

    const handleOnChange = useCallback(
        (newValues: string[]) => {
            const maxSelectedOptions = maxSelected || members.length;
            if (newValues.length <= maxSelectedOptions) {
                const selectedMembers = members.filter(
                    (member) => newValues.includes(member.uid as string),
                );
                onChange(selectedMembers);
            }
        },
        [maxSelected, members, onChange],
    );

    if (!isFetched) {
        return (
            <>
                <LoadingShape width="30%" />
                <LoadingShape width="40%" />
                <LoadingShape width="10%" />
            </>
        );
    }

    return (
        <StyledCheckBoxGroup
            {...rest}
            hideLabel
            options={options}
            value={value}
            onChange={handleOnChange}
        />
    );
};

export default TeamMemberSelect;
